import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  LifeBuoy,
  DollarSign,
  AlertTriangle,
  FileText,
  Target,
  Save,
  Download,
  Info,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';

interface DecisionOutputsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  onDecisionsUpdate: (decisions: DecisionState) => void;
}

interface DecisionState {
  growthFocus: string;
  supportNeeded: string;
  compensationInput: string;
  riskFlag: string;
  riskDetails: string;
}

const DecisionOutputsPanel = ({
  employee,
  reviewData,
  onDecisionsUpdate,
}: DecisionOutputsPanelProps) => {
  const { t } = useTranslation();

  const [decisions, setDecisions] = useState<DecisionState>(() => {
    try {
      if (reviewData.snapshot?.decisionsSummary) {
        const saved = JSON.parse(reviewData.snapshot.decisionsSummary);
        return {
          growthFocus: saved.growthFocus || '',
          supportNeeded: saved.supportNeeded || '',
          compensationInput: saved.compensationInput || '',
          riskFlag: saved.riskFlag || 'none',
          riskDetails: saved.riskDetails || '',
        };
      }
    } catch { /* ignore parse errors */ }
    return {
      growthFocus: '',
      supportNeeded: '',
      compensationInput: '',
      riskFlag: 'none',
      riskDetails: '',
    };
  });

  const [isSaved, setIsSaved] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);

  const riskFlagOptions = [
    {
      value: 'none',
      label: t('performanceSnapshot.decisions.riskOptions.none'),
    },
    {
      value: 'performance',
      label: t('performanceSnapshot.decisions.riskOptions.performance'),
    },
    {
      value: 'retention',
      label: t('performanceSnapshot.decisions.riskOptions.retention'),
    },
    {
      value: 'engagement',
      label: t('performanceSnapshot.decisions.riskOptions.engagement'),
    },
    {
      value: 'skill-gap',
      label: t('performanceSnapshot.decisions.riskOptions.skillGap'),
    },
  ];

  const handleFieldChange = (field: keyof DecisionState, value: string) => {
    const updatedDecisions = {
      ...decisions,
      [field]: value,
    };
    setDecisions(updatedDecisions);
    setIsSaved(false);
  };

  const handleSave = () => {
    onDecisionsUpdate(decisions);
    setIsSaved(true);
    setShowSaveToast(true);
    setTimeout(() => {
      setIsSaved(false);
      setShowSaveToast(false);
    }, 3000);
  };

  const isComplete =
    decisions.growthFocus.trim().length > 0 &&
    decisions.supportNeeded.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <CheckCircle size={24} />
              <span>{t('performanceSnapshot.decisions.title')}</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('performanceSnapshot.decisions.subtitle', {
                name: employee.name,
              })}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={!isComplete}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isSaved
                ? 'bg-green-600 text-white'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSaved ? <CheckCircle2 size={16} /> : <Save size={16} />}
            <span>
              {isSaved
                ? t('performanceSnapshot.decisions.saved')
                : t('performanceSnapshot.decisions.saveDecisions')}
            </span>
          </button>
        </div>

        {/* Completion Status */}
        <div className="flex items-center space-x-2 text-sm">
          {isComplete ? (
            <>
              <CheckCircle2 size={16} className="text-green-500" />
              <span className="text-green-600 font-medium">
                {t('performanceSnapshot.decisions.allComplete')}
              </span>
            </>
          ) : (
            <>
              <AlertCircle size={16} className="text-amber-500" />
              <span className="text-amber-600 font-medium">
                {t('performanceSnapshot.decisions.requiredFields')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Growth Focus */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.growthFocus.label')}</span>
              <span className="text-red-500 text-sm">*</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.growthFocus.description')}
            </p>
            <textarea
              value={decisions.growthFocus}
              onChange={(e) => handleFieldChange('growthFocus', e.target.value)}
              placeholder={t('performanceSnapshot.decisions.growthFocus.placeholder')}
              rows={6}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.growthFocus.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Support Needed */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <LifeBuoy size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.supportNeeded.label')}</span>
              <span className="text-red-500 text-sm">*</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.supportNeeded.description')}
            </p>
            <textarea
              value={decisions.supportNeeded}
              onChange={(e) =>
                handleFieldChange('supportNeeded', e.target.value)
              }
              placeholder={t('performanceSnapshot.decisions.supportNeeded.placeholder')}
              rows={6}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.supportNeeded.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Compensation Input */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <DollarSign size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.compensationInput.label')}</span>
              <span className="text-muted-foreground text-sm font-normal">
                ({t('performanceSnapshot.decisions.compensationInput.optional')})
              </span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.compensationInput.description')}
            </p>
            <textarea
              value={decisions.compensationInput}
              onChange={(e) =>
                handleFieldChange('compensationInput', e.target.value)
              }
              placeholder={t('performanceSnapshot.decisions.compensationInput.placeholder')}
              rows={5}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.compensationInput.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Risk Flag */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2">
              {t('performanceSnapshot.decisions.riskFlag.label')}
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              {t('performanceSnapshot.decisions.riskFlag.description')}
            </p>

            <select
              value={decisions.riskFlag}
              onChange={(e) => handleFieldChange('riskFlag', e.target.value)}
              className="w-full p-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              {riskFlagOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {decisions.riskFlag !== 'none' && (
              <div className="mt-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t('performanceSnapshot.decisions.riskFlag.detailsLabel')}
                </label>
                <textarea
                  value={decisions.riskDetails}
                  onChange={(e) =>
                    handleFieldChange('riskDetails', e.target.value)
                  }
                  placeholder={t('performanceSnapshot.decisions.riskFlag.detailsPlaceholder')}
                  rows={5}
                  className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                />
                <div className="text-xs text-muted-foreground mt-2">
                  {decisions.riskDetails.length || 0}{' '}
                  {t('performanceSnapshot.decisions.characters')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
        <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
          <FileText size={18} />
          <span>{t('performanceSnapshot.decisions.summary.title')}</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Target size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.growthFocus.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.growthFocus
                ? `${decisions.growthFocus.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.notSpecified')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <LifeBuoy size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.supportNeeded.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.supportNeeded
                ? `${decisions.supportNeeded.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.notSpecified')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.compensationInput.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.compensationInput
                ? `${decisions.compensationInput.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.noInput')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.riskFlag.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {riskFlagOptions.find((opt) => opt.value === decisions.riskFlag)
                ?.label || t('performanceSnapshot.decisions.summary.notAssessed')}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Info size={16} className="text-blue-500" />
          <span className="text-sm text-muted-foreground">
            {t('performanceSnapshot.decisions.saveNote')}
          </span>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors">
            <Download size={16} />
            <span>{t('performanceSnapshot.decisions.exportReview')}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={!isComplete}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            <span>{t('performanceSnapshot.decisions.saveFinalize')}</span>
          </button>
        </div>
      </div>

      {/* Save Toast */}
      {showSaveToast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 z-50">
          <CheckCircle2 size={18} />
          <span className="text-sm font-medium">
            {t('performanceSnapshot.decisions.savedSuccess')}
          </span>
        </div>
      )}
    </div>
  );
};

export default DecisionOutputsPanel;
